// packages/ui/src/pages/Settings.tsx
// Tabbed settings page with channel, provider, automation, and node config

import { useState } from 'react'
import { ChannelSettings, type Channel } from '../components/settings/ChannelSettings'
import { ProviderSettings, type Provider } from '../components/settings/ProviderSettings'
import { AutomationSettings, type Automation, type RateLimit } from '../components/settings/AutomationSettings'
import { NodeSettings, type NodeInfo } from '../components/settings/NodeSettings'

export type { Channel, Provider, Automation, RateLimit, NodeInfo }

type SettingsTab = 'channels' | 'providers' | 'automation' | 'nodes'

const tabs: { id: SettingsTab; label: string }[] = [
  { id: 'channels', label: 'Channels' },
  { id: 'providers', label: 'Providers' },
  { id: 'automation', label: 'Automation' },
  { id: 'nodes', label: 'Nodes' },
]

type UpdatePayload =
  | { id: string; enabled: boolean }
  | { id: string; apiKey?: string; model?: string }

interface SettingsProps {
  channels: Channel[]
  providers: Provider[]
  automations: Automation[]
  rateLimit: RateLimit
  nodes: NodeInfo[]
  onUpdate: (type: string, payload: UpdatePayload) => void
}

export function Settings({
  channels,
  providers,
  automations,
  rateLimit,
  nodes,
  onUpdate,
}: SettingsProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('channels')

  return (
    <div data-testid="settings-page" className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
        <h1 className="text-2xl font-bold">Settings</h1>
      </div>

      {/* Tab Bar */}
      <div className="flex border-b border-gray-800 px-6" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-blue-400 border-blue-500'
                : 'text-gray-400 border-transparent hover:text-gray-200 hover:border-gray-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'channels' && (
          <ChannelSettings channels={channels} onUpdate={onUpdate} />
        )}
        {activeTab === 'providers' && (
          <ProviderSettings providers={providers} onUpdate={onUpdate} />
        )}
        {activeTab === 'automation' && (
          <AutomationSettings
            automations={automations}
            rateLimit={rateLimit}
            onUpdate={onUpdate}
          />
        )}
        {activeTab === 'nodes' && <NodeSettings nodes={nodes} />}
      </div>
    </div>
  )
}
